import byteToMBRounded from "@gef-ui/utils/byteToMBRounded";
import test from "ramda/es/test";
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import mapIndexed from "ramda-extension/es/mapIndexed";
import rejectNil from "ramda-extension/es/rejectNil";
const mapRejectNit = (fn, value)=>rejectNil(mapIndexed(fn, value));
/**
 * Validate max file size for FileInput
 *
 * @documented
 * @category FileUpload
 * @import import { maxFileSize } from '@gef-ui/validations/validationFunctions/fileUpload'
 * @param {Object} input
 * @param {String} [input.fieldMessageKey=uploadHasError] - default message for field
 * @param {String} [input.fileMessageKey=wrongSize] - default message for file
 * @param {Number} [input.maxSize] - max size in bytes
 * @returns {String} errMessage
 *
 */ const maxFileSize = ({ fieldMessageKey = 'uploadHasError', fileMessageKey = 'wrongSize', maxSize })=>({ fieldValue = [] })=>{
        const result = mapRejectNit(({ size }, index)=>maxSize < size ? {
                messageKey: fileMessageKey,
                index,
                sizeMb: byteToMBRounded(size)
            } : undefined, fieldValue);
        return {
            messageKey: isNotEmpty(result) ? fieldMessageKey : false,
            metaInfo: result
        };
    };
/**
 * Validate total max file size for FileInput
 *
 * @documented
 * @category FileUpload
 * @import import { totalMaxFileSize } from '@gef-ui/validations/validationFunctions/fileUpload'
 * @param {Object} input
 * @param {String} [input.fieldMessageKey=uploadTotalMaxSize] - default message for field
 * @param {Number} [input.maxSize] - max size in bytes
 * @returns {String} errMessage
 *
 */ const totalMaxFileSize = ({ fieldMessageKey = 'uploadTotalMaxSize', maxSize })=>({ fieldValue = [] })=>{
        const totalSize = fieldValue.reduce((acc, { size = 0 })=>acc + size, 0);
        return totalSize > maxSize ? {
            messageKey: fieldMessageKey,
            messageArgs: {
                sizeMb: byteToMBRounded(totalSize),
                maxSizeMb: byteToMBRounded(maxSize)
            }
        } : false;
    };
/**
 * Accepted file extensions for FileInput
 *
 * @documented
 * @category FileUpload
 * @import import { acceptedExtension } from '@gef-ui/validations/validationFunctions/fileUpload'
 * @param {Object} input
 * @param {String} [input.fieldMessageKey=uploadHasError] - default message for field
 * @param {String} [input.fileMessageKey=wrongExtension] - default message for file
 * @param {RegExp} [input.extension] - regexp for file extension
 * @returns {String} errMessage
 *
 */ const acceptedExtension = ({ fieldMessageKey = 'uploadHasError', fileMessageKey = 'wrongExtension', extension })=>({ fieldValue = [] })=>{
        let result;
        if (extension instanceof RegExp) {
            result = mapRejectNit(({ name }, index)=>!test(extension, name) ? {
                    messageKey: fileMessageKey,
                    index
                } : undefined, fieldValue);
        } else {
            const parsed = extension.split(',');
            const fileExts = parsed.filter((ext)=>ext.startsWith('.'));
            const mimesWild = parsed.filter((ext)=>ext.includes('/*')).map((mime)=>mime.split('/').shift()).filter((mime)=>mime !== undefined);
            const mimesStrict = parsed.filter((ext)=>ext.includes('/') && !ext.includes('*'));
            result = fieldValue.map(({ name, type }, index)=>{
                const hasExts = fileExts.some((ext)=>name.toLowerCase().endsWith(ext.toLowerCase()));
                const hasWildMime = mimesWild.some((mime)=>type.startsWith(mime));
                const hasStrictMime = mimesStrict.some((mime)=>type === mime);
                if (!hasExts && !hasWildMime && !hasStrictMime) {
                    return {
                        messageKey: fileMessageKey,
                        index
                    };
                }
                return undefined;
            }).filter((file)=>file);
        }
        return {
            messageKey: isNotEmpty(result) ? fieldMessageKey : false,
            metaInfo: result
        };
    };
/**
 * Validate max number of files for FileInput
 *
 * @documented
 * @category FileUpload
 * @import import { maxNumOfFiles } from '@gef-ui/validations/validationFunctions/fileUpload'
 * @param {Object} input
 * @param {Number} [input.numOfFiles=1] - max number of files
 * @param {String} [input.fieldMessageKey=uploadMaxNumOfFiles] - default message for field
 * @returns {String} errMessage
 *
 */ const maxNumOfFiles = ({ numOfFiles = 1, fieldMessageKey = 'uploadMaxNumOfFiles' })=>({ fieldValue = [] })=>{
        if (fieldValue.length > numOfFiles) {
            return {
                messageKey: fieldMessageKey,
                messageArgs: {
                    numOfFiles
                }
            };
        }
    };
export default {
    maxFileSize,
    acceptedExtension,
    totalMaxFileSize,
    maxNumOfFiles
};
